import { useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { AlertBanner } from "@similarweb/ui-components";

import { CONTACTS_PER_COMPANY_LIMIT_TRANSLATIONS } from "app/pages/sales-intelligence/common-components/contacts-per-company-limit-input/constants";
import { ContactsPerCompanyLimitInput } from "app/pages/sales-intelligence/common-components/contacts-per-company-limit-input/ContactsPerCompanyLimitInput";
import { FiltersChip } from "app/pages/sales-intelligence/common-components/filters-panel";
import { FilterItemCard } from "app/pages/sales-intelligence/common-components/filters-panel/components/FilterItemCard/FilterItemCard";
import { FilterItemClearButton } from "app/pages/sales-intelligence/common-components/filters-panel/components/FilterItemClearButton/FilterItemClearButton";
import { FilterResultContainer } from "app/pages/sales-intelligence/common-components/filters-panel/components/styles";
import { usePlaybooksTrackingService } from "app/pages/sales-intelligence/modules/playbooks/hooks/usePlaybooksTrackingService";

import { DEFAULT_PLAYBOOK_CONTACTS_COUNT, MAX_PLAYBOOK_CONTACTS_COUNT } from "../../../../constants";

type PlaybookContactsLimitFilterCardProps = {
    value?: number;
    onChange: (value?: number) => void;
};

export const PlaybookContactsLimitFilterCard = ({ value, onChange }: PlaybookContactsLimitFilterCardProps) => {
    const { translate } = useTranslate();
    const playbooksTracking = usePlaybooksTrackingService();
    const hasValue = value !== undefined;
    const [isExpanded, setIsExpanded] = useState(!hasValue);

    const handleClear = () => {
        playbooksTracking.trackContactsLimitClear();
        onChange(undefined);
        setIsExpanded(true);
    };

    const handleContactsChange = (next?: number) => {
        playbooksTracking.trackContactsLimitChange();
        onChange(next);
    };

    const handleToggleExpand = () => {
        setIsExpanded((current) => !current);
    };

    const contactsLimitBanner = (
        <AlertBanner
            variant="info"
            data-automation="playbook-wizard-contacts-limit-alert-banner"
        >
            <AlertBanner.Image />
            <AlertBanner.Description data-automation="playbook-wizard-contacts-limit-alert-banner-description">
                {translate("si.playbooks.wizard.employee_profiles.contacts_limit.alert_banner.description")}
            </AlertBanner.Description>
        </AlertBanner>
    );

    const filterResultView = (
        <>
            {hasValue ? (
                <FilterResultContainer data-automation="playbook-wizard-contacts-limit-chips">
                    <FiltersChip
                        text={`${value} ${translate(CONTACTS_PER_COMPANY_LIMIT_TRANSLATIONS.suffix)}`}
                        onClose={handleClear}
                    />
                </FilterResultContainer>
            ) : null}
            {contactsLimitBanner}
        </>
    );

    return (
        <FilterItemCard
            dataAutomation="playbook-wizard-contacts-limit-filter"
            title={{ key: "si.lead_gen_filters.category.contacts_per_company_filter.name" }}
            tooltipText={{ key: "si.lead_gen_filters.category.contacts_per_company_filter.tooltip" }}
            isExpanded={isExpanded}
            onToggleExpand={handleToggleExpand}
            filterResultView={filterResultView}
            renderActions={() => (hasValue ? <FilterItemClearButton onClick={handleClear} /> : null)}
        >
            <ContactsPerCompanyLimitInput
                value={value}
                defaultPlaceholder={DEFAULT_PLAYBOOK_CONTACTS_COUNT}
                maxValue={MAX_PLAYBOOK_CONTACTS_COUNT}
                maxValueErrorMessage={translate("si.playbooks.wizard.employee_profiles.contacts_limit.max_error", {
                    max: MAX_PLAYBOOK_CONTACTS_COUNT,
                })}
                onChange={handleContactsChange}
                dataAutomation="playbook-wizard-contacts-limit-input"
            />
        </FilterItemCard>
    );
};
